import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import * as interestsApi from "@/api/interests";
import type {
  ApiResponse,
  IdentityBoard,
  InterestType,
  RefInterest,
  SocialStyle,
  ReligiousInvolvement,
  LifestyleBlocksData,
} from "@/api/types";
import { Loader2, X } from "lucide-react";
import clsx from "clsx";
import ProposeInterestInput from "@/components/board/ProposeInterestInput";

interface LifestyleBlockProps {
  onComplete: () => void;
}

// Phase 13.6 — touchstones live in 4 InterestType buckets, displayed under
// the same human-readable headers the lab used pre-migration.
const TOUCHSTONE_BUCKETS: {
  type: InterestType;
  header: string;
  proposePlaceholder: string;
}[] = [
  { type: "touchstone_film", header: "Bollywood", proposePlaceholder: "Add a film…" },
  { type: "touchstone_tv", header: "TV Shows", proposePlaceholder: "Add a show…" },
  { type: "touchstone_music", header: "Music", proposePlaceholder: "Add an artist or band…" },
  { type: "touchstone_cultural", header: "Cultural", proposePlaceholder: "Add a cultural moment…" },
];

// Connection intents stay hardcoded (Decision D4) — fixed taxonomy mapped to
// matching weights, never user-extensible.
const CONNECTION_INTENTS = [
  { value: "friendship", label: "Friendship", emoji: "🤝" },
  { value: "professional", label: "Professional Networking", emoji: "💼" },
  { value: "mentorship", label: "Mentorship", emoji: "🎓" },
  { value: "hobby_partners", label: "Hobby Partners", emoji: "🎯" },
  { value: "local_community", label: "Local Community", emoji: "🏘️" },
  { value: "dating", label: "Dating", emoji: "💕" },
];

const SOCIAL_STYLES: {
  value: SocialStyle;
  label: string;
  description: string;
}[] = [
  {
    value: "large_gatherings",
    label: "Large Gatherings",
    description: "Prefer big events and parties",
  },
  {
    value: "small_groups",
    label: "Small Groups",
    description: "Prefer intimate groups of friends",
  },
  {
    value: "one_on_one",
    label: "One on One",
    description: "Prefer 1:1 deep connections",
  },
  {
    value: "family_events",
    label: "Family Events",
    description: "Prefer family-oriented gatherings",
  },
  {
    value: "online_first",
    label: "Online First",
    description: "Prefer digital connection",
  },
];

const RELIGIOUS_INVOLVEMENT_OPTIONS: {
  value: ReligiousInvolvement;
  label: string;
  description: string;
}[] = [
  {
    value: "active_weekly",
    label: "Very Active",
    description: "Weekly participation, deeply involved",
  },
  { value: "regular", label: "Regular", description: "2-3 times a month" },
  {
    value: "occasional",
    label: "Occasional",
    description: "Festivals and special occasions",
  },
  {
    value: "not_active",
    label: "Not Active",
    description: "Don't actively participate",
  },
  {
    value: "prefer_not_say",
    label: "Prefer Not to Say",
    description: "Keep this private",
  },
];

export default function LifestyleBlock({ onComplete }: LifestyleBlockProps) {
  const queryClient = useQueryClient();

  // Fetch existing board data
  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: ["identity-board"],
    queryFn: () => api.get<ApiResponse<IdentityBoard>>("/identity-board"),
  });

  // Phase 13.6 — pull touchstones / spiritual practices / sewa types from
  // RefInterest (seeded in 1A.2) instead of hardcoded arrays. Three distinct
  // queries so caching keys stay clean and a search-bar refetch in one
  // section doesn't invalidate the others.
  const { data: touchstonesData } = useQuery({
    queryKey: ["interests", { category: "touchstones" }],
    queryFn: () => interestsApi.listInterestsByCategory("touchstones"),
  });
  const { data: spiritualData } = useQuery({
    queryKey: ["interests", { type: "spiritual_practice" }],
    queryFn: () => interestsApi.listInterestsByType("spiritual_practice"),
  });
  const { data: sewaData } = useQuery({
    queryKey: ["interests", { type: "sewa_type" }],
    queryFn: () => interestsApi.listInterestsByType("sewa_type"),
  });

  // User's own pending proposals — drive the "review" badge on their pills.
  const { data: myProposalsData } = useQuery({
    queryKey: ["my-proposals", "pending"],
    queryFn: () => interestsApi.getMyProposals({ status: "pending" }),
  });

  const board = boardData?.data;
  const lifestyleBlocks = board?.lifestyle;
  const touchstoneItems = useMemo(
    () => touchstonesData?.data?.items || [],
    [touchstonesData],
  );
  const spiritualItems = useMemo(
    () => spiritualData?.data?.items || [],
    [spiritualData],
  );
  const sewaItems = useMemo(() => sewaData?.data?.items || [], [sewaData]);
  const myPendingProposals = useMemo(
    () => myProposalsData?.data?.items || [],
    [myProposalsData],
  );
  const pendingCodes = useMemo(
    () => new Set(myPendingProposals.map((p) => p.code)),
    [myPendingProposals],
  );

  // Form state
  const [touchstones, setTouchstones] = useState<string[]>([]);
  const [connectionIntents, setConnectionIntents] = useState<string[]>([]);
  const [socialStyle, setSocialStyle] = useState<SocialStyle | "">("");
  const [socialStyleNote, setSocialStyleNote] = useState("");
  const [spiritualPractices, setSpiritualPractices] = useState<string[]>([]);
  const [religiousInvolvement, setReligiousInvolvement] = useState<
    ReligiousInvolvement | ""
  >("");
  const [sewaActive, setSewaActive] = useState(false);
  const [sewaInterests, setSewaInterests] = useState<string[]>([]);
  const [openToReligiousConnect, setOpenToReligiousConnect] = useState(false);

  // Locally-tracked freshly-proposed interests so a just-submitted pending
  // pill stays visible before the next refetch.
  const [proposedInterests, setProposedInterests] = useState<RefInterest[]>([]);

  // Resolve a saved label/code into a canonical RefInterest. Tries the
  // catalog row first, then the user's pending list, then anything they
  // proposed in this session.
  const resolveCode = useMemo(() => {
    const pool = [
      ...touchstoneItems,
      ...spiritualItems,
      ...sewaItems,
      ...myPendingProposals,
      ...proposedInterests,
    ];
    return (raw: string): string | null => {
      const lower = raw.toLowerCase();
      const match = pool.find(
        (i) =>
          i.code === raw ||
          i.canonicalNameEn.toLowerCase() === lower ||
          i.searchAliases.includes(lower),
      );
      return match ? match.code : null;
    };
  }, [touchstoneItems, spiritualItems, sewaItems, myPendingProposals, proposedInterests]);

  // Initialize from existing board data. Saved entries are either codes
  // (current format) or legacy labels — resolve both shapes through the
  // catalog so re-editing an old board doesn't drop selections.
  useEffect(() => {
    if (!lifestyleBlocks) return;
    const resolveAll = (xs: string[] | undefined): string[] =>
      (xs || [])
        .map((raw) => resolveCode(raw) ?? raw)
        .filter((c, i, arr) => arr.indexOf(c) === i);

    setTouchstones(resolveAll(lifestyleBlocks.touchstones));
    setConnectionIntents(lifestyleBlocks.connectionIntents || []);
    setSocialStyle(lifestyleBlocks.socialStyle || "");
    setSocialStyleNote(lifestyleBlocks.socialStyleNote || "");
    setSpiritualPractices(resolveAll(lifestyleBlocks.spiritualPractices));
    setReligiousInvolvement(lifestyleBlocks.religiousInvolvement || "");
    setSewaActive(lifestyleBlocks.sewaActive || false);
    setSewaInterests(resolveAll(lifestyleBlocks.sewaInterests));
    setOpenToReligiousConnect(lifestyleBlocks.openToReligiousConnect || false);
  }, [lifestyleBlocks, resolveCode]);

  // Lookup helpers for rendering pills after we've stored codes in state.
  const lookupInterest = useMemo(() => {
    const map = new Map<string, RefInterest>();
    for (const i of touchstoneItems) map.set(i.code, i);
    for (const i of spiritualItems) map.set(i.code, i);
    for (const i of sewaItems) map.set(i.code, i);
    for (const p of myPendingProposals) if (!map.has(p.code)) map.set(p.code, p);
    for (const p of proposedInterests) if (!map.has(p.code)) map.set(p.code, p);
    return (code: string) => map.get(code);
  }, [touchstoneItems, spiritualItems, sewaItems, myPendingProposals, proposedInterests]);

  // Group touchstones into the 4 visual buckets (film/tv/music/cultural).
  const touchstonesByBucket = useMemo(() => {
    const groups: Record<string, RefInterest[]> = {
      touchstone_film: [],
      touchstone_tv: [],
      touchstone_music: [],
      touchstone_cultural: [],
    };
    for (const item of touchstoneItems) {
      if (groups[item.interestType]) {
        groups[item.interestType].push(item);
      }
    }
    return groups;
  }, [touchstoneItems]);

  // Update mutation - use block-specific endpoint
  const updateLifestyleBlocks = useMutation({
    mutationFn: async (data: LifestyleBlocksData) => {
      return api.patch<ApiResponse<IdentityBoard>>(
        "/identity-board/blocks/lifestyle",
        data,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["identity-board"] });
      queryClient.invalidateQueries({
        queryKey: ["identity-board-completeness"],
      });
      onComplete();
    },
  });

  const toggleTouchstone = (code: string) => {
    setTouchstones((prev) =>
      prev.includes(code) ? prev.filter((t) => t !== code) : [...prev, code],
    );
  };

  const toggleIntent = (intent: string) => {
    setConnectionIntents((prev) =>
      prev.includes(intent) ? prev.filter((i) => i !== intent) : [...prev, intent],
    );
  };

  const togglePractice = (code: string) => {
    setSpiritualPractices((prev) =>
      prev.includes(code) ? prev.filter((p) => p !== code) : [...prev, code],
    );
  };

  const toggleSewaInterest = (code: string) => {
    setSewaInterests((prev) =>
      prev.includes(code) ? prev.filter((i) => i !== code) : [...prev, code],
    );
  };

  // Phase 13.6 — when ProposeInterestInput resolves (matched/pending/auto-approved),
  // cache the canonical row locally and add its code to the appropriate selection list.
  const handleProposed = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => (interest: RefInterest) => {
    setProposedInterests((prev) =>
      prev.find((p) => p.code === interest.code) ? prev : [...prev, interest],
    );
    setter((prev) =>
      prev.includes(interest.code) ? prev : [...prev, interest.code],
    );
  };

  const handleSubmit = () => {
    updateLifestyleBlocks.mutate({
      touchstones,
      connectionIntents,
      socialStyle: socialStyle || undefined,
      socialStyleNote: socialStyleNote.trim() || undefined,
      spiritualPractices,
      religiousInvolvement: religiousInvolvement || undefined,
      sewaActive,
      sewaInterests,
      openToReligiousConnect,
    });
  };

  const hasRequiredFields = connectionIntents.length > 0;

  if (boardLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Touchstones */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label mb-0">Cultural Touchstones</label>
          <span className="text-xs text-gray-500">
            {touchstones.length} selected
          </span>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Select movies, shows, music, or cultural moments that shaped you
        </p>

        {/* Selected touchstones */}
        {touchstones.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {touchstones.map((code) => {
              const interest = lookupInterest(code);
              const label = interest?.canonicalNameEn ?? code;
              const isPending = pendingCodes.has(code);
              return (
                <span
                  key={code}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-strong-light text-strong-dark rounded-full text-sm"
                >
                  {interest?.emoji && <span>{interest.emoji}</span>}
                  {label}
                  {isPending && (
                    <span
                      className="text-[10px] text-amber-700"
                      title="Pending review — visible to others once approved"
                    >
                      (review)
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleTouchstone(code)}
                    className="hover:text-strong"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* Touchstone categories — fetched from RefInterest, grouped into the
            4 InterestType buckets the lab used to hardcode. */}
        <div className="space-y-4">
          {TOUCHSTONE_BUCKETS.map((bucket) => {
            const items = touchstonesByBucket[bucket.type] || [];
            return (
              <div key={bucket.type}>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {bucket.header}
                </span>
                <div className="flex flex-wrap gap-2 mt-2 items-center">
                  {items
                    .filter((item) => !touchstones.includes(item.code))
                    .map((item) => (
                      <button
                        key={item.code}
                        type="button"
                        onClick={() => toggleTouchstone(item.code)}
                        className="px-3 py-1.5 rounded-full text-sm border border-gray-300 text-gray-700 hover:border-strong hover:bg-strong-light transition-colors"
                      >
                        {item.emoji && <span className="mr-1">{item.emoji}</span>}
                        {item.canonicalNameEn}
                      </button>
                    ))}
                  <ProposeInterestInput
                    interestType={bucket.type}
                    category="touchstones"
                    excludeCodes={touchstones}
                    placeholder={bucket.proposePlaceholder}
                    onAdd={handleProposed(setTouchstones)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Connection Intents */}
      <div>
        <label className="label">What are you looking for? *</label>
        <p className="text-sm text-gray-500 mb-3">Select all that apply</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {CONNECTION_INTENTS.map((intent) => (
            <button
              key={intent.value}
              type="button"
              onClick={() => toggleIntent(intent.value)}
              className={clsx(
                "p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3",
                connectionIntents.includes(intent.value)
                  ? "border-primary-500 bg-primary-50"
                  : "border-gray-200 hover:border-gray-300",
              )}
            >
              <span className="text-xl">{intent.emoji}</span>
              <span className="font-medium">{intent.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Social Style */}
      <div>
        <label className="label">Social Style</label>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          {SOCIAL_STYLES.map((style) => (
            <button
              key={style.value}
              type="button"
              onClick={() => setSocialStyle(style.value)}
              className={clsx(
                "p-3 rounded-lg border-2 text-left transition-all",
                socialStyle === style.value
                  ? "border-primary-500 bg-primary-50"
                  : "border-gray-200 hover:border-gray-300",
              )}
            >
              <span className="font-medium block">{style.label}</span>
              <span className="text-xs text-gray-500">{style.description}</span>
            </button>
          ))}
        </div>

        {/*
          The note sits UNDER the enum, not instead of it. The enum is what the
          matcher scores; this is the part it flattens — two people can both be
          `small_groups` and mean completely different evenings.

          Owner-only: it is not shown on somebody else's board in discovery,
          because free text is where a phone number or an employer ends up.
        */}
        <label className="label mt-4 block" htmlFor="social-style-note">
          In your own words (optional)
        </label>
        <p className="text-sm text-gray-500 mb-2">
          How do you actually like to spend time with people? Only you see this
          — it is not shown on your board to others.
        </p>
        <textarea
          id="social-style-note"
          value={socialStyleNote}
          onChange={(e) => setSocialStyleNote(e.target.value.slice(0, 280))}
          rows={3}
          placeholder="Temple on Sundays, then whoever's free for chai after"
          className="input w-full"
        />
        <p className="text-xs text-gray-400 mt-1 tabular-nums">
          {socialStyleNote.length}/280
        </p>
      </div>

      {/* Spiritual Practices */}
      <div>
        <label className="label">Spiritual Practices (optional)</label>
        <p className="text-sm text-gray-500 mb-3">
          Select all that resonate with you
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          {spiritualItems.map((item) => {
            const isSelected = spiritualPractices.includes(item.code);
            const isPending = pendingCodes.has(item.code);
            return (
              <button
                key={item.code}
                type="button"
                onClick={() => togglePractice(item.code)}
                className={clsx(
                  "px-4 py-2 rounded-full text-sm border-2 transition-colors",
                  isSelected
                    ? "border-primary-500 bg-primary-50 text-primary-700"
                    : "border-gray-300 text-gray-700 hover:border-gray-400",
                )}
              >
                {item.emoji && <span className="mr-1">{item.emoji}</span>}
                {item.canonicalNameEn}
                {isSelected && isPending && (
                  <span className="ml-1 text-[10px] text-amber-700">
                    (review)
                  </span>
                )}
              </button>
            );
          })}
          <ProposeInterestInput
            interestType="spiritual_practice"
            category="spiritual"
            excludeCodes={spiritualPractices}
            placeholder="Add a spiritual practice…"
            onAdd={handleProposed(setSpiritualPractices)}
          />
        </div>
      </div>

      {/* Religious Involvement */}
      <div className="border-t pt-6">
        <label className="label">
          Religious Community Involvement (optional)
        </label>
        <p className="text-sm text-gray-500 mb-4">
          How involved are you with religious or spiritual communities?
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {RELIGIOUS_INVOLVEMENT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setReligiousInvolvement(option.value)}
              className={clsx(
                "p-3 rounded-lg border-2 text-left transition-all",
                religiousInvolvement === option.value
                  ? "border-primary-500 bg-primary-50"
                  : "border-gray-200 hover:border-gray-300",
              )}
            >
              <span className="font-medium block">{option.label}</span>
              <span className="text-xs text-gray-500">
                {option.description}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Sewa / Volunteering */}
      <div>
        <label className="label">Sewa / Volunteering (optional)</label>
        <p className="text-sm text-gray-500 mb-4">
          Are you interested in community service through religious or cultural
          organizations?
        </p>

        <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 hover:border-gray-300 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={sewaActive}
            onChange={(e) => setSewaActive(e.target.checked)}
            className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div>
            <span className="font-medium">I'm actively interested in Sewa</span>
            <p className="text-xs text-gray-500">
              I want to volunteer and serve the community
            </p>
          </div>
        </label>

        {sewaActive && (
          <div className="ml-4 pl-4 border-l-2 border-primary-200">
            <p className="text-sm text-gray-600 mb-3">
              What types of sewa interest you?
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              {sewaItems.map((item) => {
                const isSelected = sewaInterests.includes(item.code);
                const isPending = pendingCodes.has(item.code);
                return (
                  <button
                    key={item.code}
                    type="button"
                    onClick={() => toggleSewaInterest(item.code)}
                    className={clsx(
                      "px-3 py-1.5 rounded-full text-sm border-2 transition-colors",
                      isSelected
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : "border-gray-300 text-gray-700 hover:border-gray-400",
                    )}
                  >
                    {item.emoji && <span className="mr-1">{item.emoji}</span>}
                    {item.canonicalNameEn}
                    {isSelected && isPending && (
                      <span className="ml-1 text-[10px] text-amber-700">
                        (review)
                      </span>
                    )}
                  </button>
                );
              })}
              <ProposeInterestInput
                interestType="sewa_type"
                category="sewa"
                excludeCodes={sewaInterests}
                placeholder="Add a type of sewa…"
                onAdd={handleProposed(setSewaInterests)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Religious Connection Opt-in */}
      <div>
        <label className="flex items-center gap-3 p-4 rounded-lg border-2 border-gray-200 hover:border-gray-300 cursor-pointer bg-gray-50">
          <input
            type="checkbox"
            checked={openToReligiousConnect}
            onChange={(e) => setOpenToReligiousConnect(e.target.checked)}
            className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div>
            <span className="font-medium">
              Open to connecting based on religious beliefs
            </span>
            <p className="text-xs text-gray-500">
              Allow matching with others who share similar religious or
              spiritual interests
            </p>
          </div>
        </label>
      </div>

      {/* Submit */}
      <div className="pt-4">
        <button
          onClick={handleSubmit}
          disabled={!hasRequiredFields || updateLifestyleBlocks.isPending}
          className="btn-primary w-full py-3 disabled:opacity-50"
        >
          {updateLifestyleBlocks.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            "Complete Your Board"
          )}
        </button>
        {!hasRequiredFields && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            Please select at least one connection intent
          </p>
        )}
        {updateLifestyleBlocks.isError && (
          <p className="text-sm text-red-600 mt-2 text-center">
            Failed to save. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
